import React from 'react'

import { useLocation, useNavigate } from 'react-router'

import { startOfDay } from '@/lib/calendarWeekdays'
import { V1SMQueryItem } from '@/lib/api/lib'

import { AppContext } from '@/components/base/appcontext'
import { appendReferrer } from '@/lib/referrerState'

type HeatmapValue = {
    date: string
    count: number
    content: number
}

type TrainingHistoryProps = {
    history: V1SMQueryItem[]
    isPublic?: boolean
}

type CellData = {
    date: Date
    count: number
    content: number
    col: number
    row: number
}

const DOT_RADIUS = 6
const DOT_DIAMETER = DOT_RADIUS * 2
const GAP = 4
const CELL = DOT_DIAMETER + GAP
const LABEL_W = 32
const HEADER_H = 18

const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', '']

const DARK_COLORS: [number, string][] = [
    [0, '#161b22'],
    [1, '#0E4517'],
    [5, '#006E12'],
    [15, '#59A626'],
    [20, '#8ED439'],
    [25, '#CACC4E'],
    [30, '#E6BF5E']
]

const LIGHT_COLORS: [number, string][] = [
    [0, '#EBEDF0'],
    [1, '#B2E38A'],
    [5, '#9FC96F'],
    [15, '#6C9923'],
    [20, '#6D711F'],
    [25, '#787724'],
    [30, '#624D22']
]

function getColor(count: number, palette: [number, string][]): string {
    let color = palette[0][1]
    for (const [threshold, c] of palette) {
        if (count >= threshold) color = c
    }
    return color
}

function buildGrid(
    startDate: Date,
    endDate: Date,
    values: HeatmapValue[]
): {
    cells: CellData[]
    totalCols: number
    monthLabels: { label: string; col: number }[]
} {
    const valueMap = new Map<string, HeatmapValue>()
    for (const v of values) {
        const key = new Date(v.date).toDateString()
        valueMap.set(key, v)
    }

    const cells: CellData[] = []
    const monthLabels: { label: string; col: number }[] = []
    const seenMonths = new Set<string>()

    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    // Align to start of week (Sunday)
    const startDay = start.getDay()
    if (startDay !== 0) {
        start.setDate(start.getDate() - startDay)
    }

    const end = new Date(endDate)
    end.setHours(23, 59, 59, 999)

    const cursor = new Date(start)
    let col = 0

    while (cursor <= end) {
        const row = cursor.getDay()

        // Track month boundaries (skip if too close to previous label)
        const monthKey = `${cursor.getFullYear()}-${cursor.getMonth()}`
        if (!seenMonths.has(monthKey)) {
            seenMonths.add(monthKey)
            const monthNames = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec'
            ]
            const prev = monthLabels[monthLabels.length - 1]
            if (!prev || col - prev.col >= 3) {
                monthLabels.push({ label: monthNames[cursor.getMonth()], col })
            }
        }

        const key = cursor.toDateString()
        const match = valueMap.get(key)

        cells.push({
            date: new Date(cursor),
            count: match?.count ?? 0,
            content: match?.content ?? 0,
            col,
            row
        })

        // Advance day
        cursor.setDate(cursor.getDate() + 1)
        if (cursor.getDay() === 0) col++
    }

    return { cells, totalCols: col + 1, monthLabels }
}

function TrainingHistory(props: TrainingHistoryProps) {
    const appContext = React.useContext(AppContext)
    const location = useLocation()
    const navigate = useNavigate()
    const { isPublic } = props

    const date = new Date()
    const startDate = new Date(date.setMonth(date.getMonth() - 4))
    const values = getTrainingHeatmap(props.history[0], startDate.getTime())

    const { cells, totalCols, monthLabels } = React.useMemo(
        () => buildGrid(startDate, new Date(), values),
        [values]
    )

    const palette = appContext.theme === 'dark' ? DARK_COLORS : LIGHT_COLORS
    const svgW = LABEL_W + totalCols * CELL
    const svgH = HEADER_H + 7 * CELL

    const handleCellClick = React.useCallback(
        (cell: CellData) => {
            if (!isPublic && cell.count > 0) {
                navigate('/schedule/list/' + cell.date.getTime(), {
                    state: appendReferrer(location.state, location.pathname)
                })
            }
        },
        [isPublic, navigate, location]
    )

    return (
        <div className="w-full rounded-xl bg-card p-2">
            <svg
                width="100%"
                viewBox={`0 0 ${svgW} ${svgH}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ display: 'block' }}
            >
                {/* Month labels */}
                {monthLabels.map((m) => (
                    <text
                        key={m.label + m.col}
                        x={LABEL_W + m.col * CELL + DOT_RADIUS}
                        y={12}
                        fill="var(--muted-foreground)"
                        fontSize={10}
                        fontFamily="var(--font-sans, system-ui, sans-serif)"
                        textAnchor="start"
                    >
                        {m.label}
                    </text>
                ))}

                {/* Day-of-week labels */}
                {DAY_LABELS.map(
                    (label, i) =>
                        label && (
                            <text
                                key={i}
                                x={LABEL_W - 6}
                                y={HEADER_H + i * CELL + DOT_RADIUS + 4}
                                fill="var(--muted-foreground)"
                                fontSize={10}
                                fontFamily="var(--font-sans, system-ui, sans-serif)"
                                textAnchor="end"
                            >
                                {label}
                            </text>
                        )
                )}

                {/* Dots */}
                {cells.map((cell) => {
                    const cx = LABEL_W + cell.col * CELL + DOT_RADIUS
                    const cy = HEADER_H + cell.row * CELL + DOT_RADIUS
                    const fill = getColor(cell.count, palette)
                    const clickable = !isPublic && cell.count > 0

                    return (
                        <circle
                            key={`${cell.col}-${cell.row}`}
                            cx={cx}
                            cy={cy}
                            r={DOT_RADIUS}
                            fill={fill}
                            style={{
                                cursor: clickable ? 'pointer' : 'default'
                            }}
                            onClick={
                                clickable
                                    ? () => handleCellClick(cell)
                                    : undefined
                            }
                        >
                            {cell.count > 0 && (
                                <title>
                                    {cell.count} workout
                                    {cell.count > 1 ? 's' : ''} on{' '}
                                    {cell.date.toLocaleDateString()}
                                </title>
                            )}
                        </circle>
                    )
                })}
            </svg>
        </div>
    )
}
export default TrainingHistory

function getTrainingHeatmap(
    history?: V1SMQueryItem,
    startDate?: number
): HeatmapValue[] {
    if (!history?.values || Object.keys(history.values).length === 0) {
        return []
    }

    const result: HeatmapValue[] = []
    for (const [key, value] of Object.entries(history.values)) {
        const timestamp = Number(key) * 1000
        if (startDate && timestamp < startDate) continue

        result.push({
            date: new Date(startOfDay(timestamp)).toDateString(),
            count: value,
            content: timestamp
        })
    }
    return result
}
